import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { morph } from '@theme/morph';
import { sectionRenderer, normalizeSpriteHrefs } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { SearchHistory } from '@theme/search-history';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

/**
 * Orchestrates the search modal (sections/search-modal.liquid).
 *
 * Idle state: renders the search history from localStorage and triggers the
 * product area <async-section> on first open — with the recently-viewed ids
 * as a /search?q=id:… `data-src` when present, otherwise the plain
 * recommendations fetch (current URL + `_async=search-extras`).
 *
 * Typed state: debounced predictive-search fetches via the Section Rendering
 * API; the returned fragment is split into three containers (suggestions and
 * collections in the primary column, products + CTA in the secondary column)
 * so the desktop columns and the mobile stacking order both match Figma.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} resetButton - The reset ("Löschen") button.
 * @property {HTMLElement} historySection - The search history section.
 * @property {HTMLElement} historyList - The search history list.
 * @property {HTMLTemplateElement} historyItemTemplate - Row template for history entries.
 * @property {HTMLElement} suggestionsContainer - Typed: matching suggestions target.
 * @property {HTMLElement} collectionsContainer - Typed: matching collections target.
 * @property {HTMLElement} productsContainer - Typed: matching products + CTA target.
 * @extends {Component<Refs>}
 */
class SearchModalComponent extends Component {
  requiredRefs = [
    'searchInput',
    'resetButton',
    'historySection',
    'historyList',
    'historyItemTemplate',
    'suggestionsContainer',
    'collectionsContainer',
    'productsContainer',
  ];

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * @returns {import('@theme/dialog').DialogComponent | null}
   */
  get dialog() {
    return /** @type {any} */ (this.closest('dialog-component'));
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const { dialog } = this;

    this.#renderHistory();

    if (dialog) {
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
    }

    // Engaging with typed results (product link, quickview trigger, view-all CTA)
    // counts as a search — capture the term without waiting for a SERP visit.
    this.addEventListener('click', this.#handleResultEngagement, { signal });

    // Measure the panel's top offset at TRIGGER time (capture phase): the
    // dialog scroll-lock sets the body position:fixed, which breaks sticky
    // header geometry — by dialog:open it is too late to measure. The trigger
    // is the header's <search-action> (opens this modal via header-actions.js).
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.target instanceof Element && event.target.closest('search-action')) {
          this.#applyTopOffset();
        }
      },
      { signal, capture: true }
    );
    document.addEventListener(
      'click',
      (event) => {
        if (event.target instanceof Element && event.target.closest('search-action')) {
          this.#applyTopOffset();
        }
      },
      { signal, capture: true }
    );

    // Viewport changes while open (mobile↔desktop switch, orientation) move
    // the header bottom edge and the carousel overflow — re-measure both. No
    // reusable global observer exists (header.js's ResizeObserver feeds the
    // unreliable --header-height vars).
    window.addEventListener('resize', this.#handleResize, { signal });

    // Product area finished loading (async-section fetch) → arrows may apply.
    this.addEventListener('async-section:loaded', () => this.#updateScrollArrows(), { signal });
  }

  #handleResize = debounce(() => {
    if (!this.dialog?.refs.dialog.open) return;
    this.#applyTopOffset();
    this.#updateScrollArrows();
  }, 100);

  /**
   * Shows the carousel arrows only when their list actually overflows —
   * called after content lands (async-section load, predictive morph) and
   * on resize.
   */
  #updateScrollArrows() {
    for (const arrows of this.querySelectorAll('.search-modal-section__arrows')) {
      const list = arrows.closest('.search-modal-section')?.querySelector('.search-modal-products__list');
      const scrollable = list instanceof HTMLElement && list.scrollWidth > list.clientWidth + 1;
      arrows.toggleAttribute('hidden', !scrollable);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * CMD+K / CTRL+K toggles the modal.
   * @param {KeyboardEvent} event
   */
  #handleKeyboardShortcut = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.#applyTopOffset();
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Positions the panel below the currently VISIBLE header bar (Figma). The
   * global --header-height/--header-group-height vars report wrong values, so
   * we measure the real bottom edge of `.header-bar` (the logo/nav row —
   * NOT the whole <header>, which on PLPs also contains the breadcrumb the
   * panel should cover): announcement + bar at page top, bar height when
   * sticky, 0 when no header is visible. Must run before the dialog
   * scroll-lock (see connectedCallback) — position: fixed on <body> breaks
   * sticky geometry.
   */
  #applyTopOffset() {
    const bar = document.querySelector('.header-bar') ?? document.querySelector('header');
    const offset = Math.max(0, Math.round(bar?.getBoundingClientRect().bottom ?? 0));
    this.closest('dialog-component')
      ?.querySelector('dialog')
      ?.style.setProperty('--search-modal-top', `${offset}px`);
  }

  #handleDialogOpen = () => {
    this.#renderHistory();
    this.#ensureProductArea();
    this.#updateScrollArrows();
    this.refs.searchInput.focus();
  };

  #handleDialogClose = () => {
    // Drop focus so the on-screen keyboard dismisses and no stray focus ring
    // lingers on the (now-hidden) input after the modal closes.
    this.refs.searchInput.blur();
    this.#resetSearch();
  };

  /* ==== Search history (section 1) ==== */

  #renderHistory() {
    const { historySection, historyList, historyItemTemplate } = this.refs;
    const terms = SearchHistory.getAll();

    const rows = terms.flatMap((term) => {
      const row = historyItemTemplate.content.cloneNode(true);
      const item = /** @type {HTMLElement | null} */ (/** @type {DocumentFragment} */ (row).querySelector('[data-term]'));
      const link = /** @type {HTMLAnchorElement | null} */ (/** @type {DocumentFragment} */ (row).querySelector('a'));
      const remove = /** @type {DocumentFragment} */ (row).querySelector('button');

      if (!item || !link || !remove) return [];

      item.dataset.term = term;
      link.textContent = term;
      link.href = this.#searchUrlFor(term);
      remove.setAttribute('aria-label', `${remove.getAttribute('aria-label')}: ${term}`);
      return [row];
    });

    historyList.replaceChildren(...rows);
    historySection.hidden = terms.length === 0;
  }

  /**
   * Clears the whole history ("Löschen" next to the heading). No confirmation.
   * @param {Event} event
   */
  clearHistory(event) {
    // The DOM removal detaches the clicked node — without stopPropagation the
    // dialog's outside-click check would treat it as a click outside and close.
    event.stopPropagation();

    SearchHistory.clear();
    this.#renderHistory();
  }

  /**
   * Removes a single history entry ((X) per row). Removing the last entry is
   * equivalent to clearing — the section hides itself.
   * @param {Event} event
   */
  removeHistoryTerm(event) {
    event.stopPropagation();

    const target = /** @type {HTMLElement} */ (event.target);
    const item = target.closest('[data-term]');
    if (!(item instanceof HTMLElement) || !item.dataset.term) return;

    SearchHistory.remove(item.dataset.term);
    this.#renderHistory();
  }

  /**
   * @param {string} term
   * @returns {string} root-relative SERP url for the term
   */
  #searchUrlFor(term) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    return url.pathname + url.search;
  }

  /* ==== Product area: recently viewed / recommendations (sections 3 + 4) ==== */

  /**
   * Fingerprint of the viewed-id list the product area was last loaded for.
   * The list can change within one page lifetime (quickview visits track via
   * bottom-up-sheet.js) — a changed fingerprint forces a refetch on reopen.
   * @type {string | null}
   */
  #productAreaFingerprint = null;

  /**
   * Loads the product area <async-section> on open. Recently-viewed products
   * win; the recommendations are the fallback (decision: the search history
   * state is irrelevant here). Unchanged viewed list → reopen is a no-op.
   */
  #ensureProductArea() {
    const asyncSection = /** @type {any} */ (this.querySelector('async-section'));
    if (!asyncSection) return;

    const viewed = RecentlyViewed.getProducts();
    const fingerprint = viewed.join(',');
    const state = asyncSection.dataset.state;

    if (state === 'loading') return;
    if (state === 'error') asyncSection.dataset.state = 'lazy';
    if (asyncSection.dataset.state === 'loaded') {
      if (this.#productAreaFingerprint === null) {
        // Eager server render (design mode) — adopt as current.
        this.#productAreaFingerprint = fingerprint;
        return;
      }
      if (this.#productAreaFingerprint === fingerprint) return;
      asyncSection.dataset.state = 'lazy';
    }

    if (viewed.length > 0) {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', viewed.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      asyncSection.dataset.src = url.pathname + url.search;
    } else {
      delete asyncSection.dataset.src;
    }

    this.#productAreaFingerprint = fingerprint;
    if (typeof asyncSection.load === 'function') asyncSection.load();
  }

  /**
   * Scrolls the product carousel belonging to the clicked headline arrow.
   * @param {Event} event
   */
  scrollProducts(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('[data-scroll-direction]');
    const list = target.closest('.search-modal-section')?.querySelector('.search-modal-products__list');

    if (!(button instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

    const direction = Number(button.dataset.scrollDirection) || 1;
    list.scrollBy({ left: direction * list.clientWidth, behavior: 'smooth' });
  }

  /* ==== Typed state: predictive results (section 5) ==== */

  /**
   * Captures the typed term when the visitor engages with a result —
   * quickview trigger or product link in the matching products, the view-all
   * CTA, or a matching collection. Runs before any navigation (localStorage
   * is synchronous).
   * @param {Event} event
   */
  #handleResultEngagement = (event) => {
    if (this.dataset.mode !== 'typed') return;

    const target = event.target instanceof Element ? event.target : null;
    const engaged = target?.closest(
      '.search-modal__typed-products a, .search-modal__typed-products button, .search-modal__typed-collections a'
    );
    if (!engaged) return;

    SearchHistory.add(this.refs.searchInput.value);
    this.#renderHistory();
  };

  search = debounce(() => {
    const term = this.refs.searchInput.value.trim();

    if (!term.length) {
      this.#resetSearch();
      return;
    }

    this.refs.resetButton.hidden = false;
    this.#getSearchResults(term);
  }, 250);

  /**
   * Escape with text in the input resets the search instead of closing the
   * dialog (the document-level `on:` delegation runs in the capture phase,
   * before DialogComponent's element-level Escape handler).
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.refs.searchInput.value) return;

    event.preventDefault();
    event.stopPropagation();
    this.#resetSearch();
    this.refs.searchInput.focus();
  };

  /**
   * @param {Event} event
   */
  resetSearch = (event) => {
    event?.stopPropagation?.();
    this.#resetSearch();
    this.refs.searchInput.focus();
  };

  /**
   * The total product count for the current term ("Alle Produkte anzeigen (N)").
   * Fetched separately — the predictive API exposes no total, so a lean
   * `_async=search-count` fragment of the real /search route delivers
   * `search.results_count` (same source as the S&D filter counts).
   * @type {{ term: string, count: string } | null}
   */
  #resultsCount = null;

  /** @param {string} term */
  async #getSearchResults(term) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'query,product,collection');
    // 9 products fill the mobile 3-column grid with complete rows (3×3)
    url.searchParams.set('resources[limit]', '9');
    url.searchParams.set('resources[limit_scope]', 'each');

    const abortController = this.#createAbortController();
    this.#updateResultsCount(term, abortController);

    try {
      const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      if (abortController.signal.aborted || !html) return;

      const fragment = new DOMParser().parseFromString(html, 'text/html');
      normalizeSpriteHrefs(fragment.body);

      this.#renderInto(this.refs.suggestionsContainer, fragment.querySelector('[data-results-suggestions]'));
      this.#renderInto(this.refs.collectionsContainer, fragment.querySelector('[data-results-collections]'));
      this.#renderInto(this.refs.productsContainer, fragment.querySelector('[data-results-products]'));

      this.dataset.mode = 'typed';
      this.#applyResultsCount(term);
      this.#updateScrollArrows();
    } catch (error) {
      if (abortController.signal.aborted) return;
      throw error;
    }
  }

  /**
   * Fetches the total result count for the term in parallel to the predictive
   * results. Cosmetic — failures are swallowed, the CTA just stays countless.
   *
   * @param {string} term
   * @param {AbortController} abortController
   */
  async #updateResultsCount(term, abortController) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    url.searchParams.set('_async', 'search-count');

    try {
      const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, true, url);
      if (abortController.signal.aborted) return;

      const marker = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-search-results-count]');
      const count = marker?.getAttribute('data-search-results-count');
      if (!count) return;

      this.#resultsCount = { term, count };
      this.#applyResultsCount(term);
    } catch {
      /* count is cosmetic */
    }
  }

  /**
   * Writes the fetched count into the CTA — called from both fetches since
   * either one can finish last.
   * @param {string} term
   */
  #applyResultsCount(term) {
    if (this.#resultsCount?.term !== term) return;

    const target = this.refs.productsContainer.querySelector('[data-results-count-target]');
    if (target) target.textContent = ` (${this.#resultsCount.count})`;
  }

  /**
   * Morphs a fragment part into its container (morph keeps DOM state stable
   * across keystrokes). A missing part empties the container — CSS hides
   * empty typed containers via :empty.
   *
   * @param {HTMLElement} container
   * @param {Element | null} source
   */
  #renderInto(container, source) {
    const next = container.cloneNode(false);
    if (source) /** @type {HTMLElement} */ (next).append(...source.childNodes);
    morph(container, /** @type {HTMLElement} */ (next));
  }

  #resetSearch() {
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.refs.searchInput.value = '';
    this.refs.resetButton.hidden = true;
    this.dataset.mode = 'idle';
  }

  #createAbortController() {
    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }
}

if (!customElements.get('search-modal-component')) {
  customElements.define('search-modal-component', SearchModalComponent);
}
